import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { InputActionMeta } from 'react-select';
import Selector from '../../components/shared/Selector/Selector';
import Field from '../../components/shared/Field/Field';
import { HistoricalSuggestion, serviceEvaluations } from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';

export type HistoricalValue = { historicalName: string; historicalNumber: string };

export type HistoricalFieldsProps = {
  value: HistoricalValue;
  onChange: (next: HistoricalValue) => void;
};

type Option = { value: string; label: string; __isNew__?: boolean };

const SUGGESTION_DELAY_MS = 300;

const clip = (text: string) => text.slice(0, EvaluationLimits.HistoricalFieldMaxLength);

const suggestionLabel = (suggestion: HistoricalSuggestion) =>
  suggestion.name && suggestion.number
    ? `${suggestion.name} (${suggestion.number})`
    : suggestion.name || suggestion.number || '';

const HistoricalFields = ({ value, onChange }: HistoricalFieldsProps) => {
  const [t] = useTranslation('evaluations');
  const [historicalInput, setHistoricalInput] = useState('');
  const [suggestions, setSuggestions] = useState<HistoricalSuggestion[]>([]);
  const latestQuery = useRef('');
  const pickedSinceTyping = useRef(false);

  const loadSuggestions = useMemo(
    () => (query: string) => {
      latestQuery.current = query;
      serviceEvaluations
        .getHistoricalSuggestions(query)
        .then(found => {
          if (latestQuery.current === query) setSuggestions(found);
        })
        .catch(reportUnexpected);
    },
    [],
  );
  const loadSuggestionsLater = useMemo(
    () => debounce(loadSuggestions, SUGGESTION_DELAY_MS),
    [loadSuggestions],
  );

  useEffect(() => () => loadSuggestionsLater.cancel(), [loadSuggestionsLater]);

  useEffect(() => {
    loadSuggestions('');
  }, [loadSuggestions]);

  const suggestionOptions: Option[] = suggestions.map(suggestion => ({
    value: suggestion.key,
    label: suggestionLabel(suggestion),
  }));

  const pickHistorical = (option: Option | null) => {
    if (!option) {
      onChange({ historicalName: '', historicalNumber: value.historicalNumber });
      return;
    }
    const suggestion = option.__isNew__
      ? undefined
      : suggestions.find(item => item.key === option.value);
    onChange({
      historicalName: clip(suggestion ? suggestion.name ?? '' : option.value.trim()),
      historicalNumber: clip(suggestion ? suggestion.number ?? '' : value.historicalNumber),
    });
  };

  const historicalValue = value.historicalName
    ? { value: value.historicalName, label: value.historicalName }
    : null;

  const typeHistorical = (text: string, meta: InputActionMeta) => {
    if (meta.action === 'set-value') pickedSinceTyping.current = true;
    if (meta.action === 'input-change') {
      pickedSinceTyping.current = false;
      setHistoricalInput(text);
      setSuggestions([]);
      latestQuery.current = text;
      loadSuggestionsLater(text);
    }
    if (meta.action === 'input-blur' && meta.prevInputValue.trim() && !pickedSinceTyping.current) {
      setHistoricalInput('');
      pickHistorical({ value: meta.prevInputValue, label: meta.prevInputValue, __isNew__: true });
    }
  };

  return (
    <>
      <Selector<Option>
        isCreatable
        isSearchable
        isClearable
        label={t('quick.historicalName', 'Projektnamn')}
        placeholder={t('quick.historicalNamePlaceholder', 'Sök eller skriv ett namn')}
        options={suggestionOptions}
        value={historicalValue}
        onChange={pickHistorical}
        getSearchString={() => historicalInput}
        formatCreateLabel={text =>
          t('quick.historicalNew', 'Nytt projekt: {{name}}', { name: text })
        }
        onInputChange={typeHistorical}
        {...({ createOptionPosition: 'first' } as object)}
      />
      <Field
        label={t('quick.historicalNumber', 'Projektnummer')}
        value={value.historicalNumber}
        onChange={text =>
          onChange({ historicalName: value.historicalName, historicalNumber: clip(text) })
        }
      />
    </>
  );
};

export default HistoricalFields;
